
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ToastComponent } from '../../components/toast/toast.component';
import { EmpresaService } from '../../services/empresa.service';
import { ModalRef, ModalService } from '../../services/modal.service';
import { SucursalService } from '../../services/sucursal.service';
import { ToastService } from '../../services/toast.service';
import { Sucursal, SucursalPayload } from '../../models/sucursal.model';
import { ImportacionProductosResumen } from '../../services/empresa.service';
import { TelefonosComponent } from '../telefonos/telefonos.component';

interface SucursalForm extends SucursalPayload {
  correlativo_boleta: number;
  correlativo_factura: number;
  correlativo_nota_credito: number;
  correlativo_nota_venta: number;
}

@Component({
  selector: 'app-sedes',
  imports: [
    FormsModule,
    RouterLink,
    ToastComponent,
    TelefonosComponent
],
  templateUrl: './sedes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sedes.component.css',
})
export class SedesComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly sucursalService = inject(SucursalService);
  private readonly empresaService = inject(EmpresaService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  empresaId = 0;
  empresaNombre = '';

  loading = false;
  error = '';
  sucursales: Sucursal[] = [];
  importandoSucursalId: number | null = null;

  private modalStack: ModalRef[] = [];
  editingSucursal: Sucursal | null = null;
  deletingSucursal: Sucursal | null = null;
  form: SucursalForm = this.getDefaultForm();

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get('empresaId'));
      this.empresaId = Number.isFinite(id) ? id : 0;
      this.loadEmpresaName();
      this.loadSucursales();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSucursales(): void {
    if (!this.empresaId) {
      this.error = 'Empresa inválida';
      return;
    }

    this.loading = true;
    this.error = '';

    this.sucursalService
      .getByEmpresa(this.empresaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sucursales) => {
          this.sucursales = sucursales;
          this.loading = false;
        },
        error: (error) => {
          this.error = error?.message || 'No se pudieron cargar las sedes';
          this.loading = false;
        },
      });
  }

  loadEmpresaName(): void {
    this.empresaService
      .getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresas) => {
          const empresa = empresas.find((item) => item.id === this.empresaId);
          this.empresaNombre = empresa?.nombre || `Empresa #${this.empresaId}`;
        },
      });
  }

  openCreateModal(modalTemplate: TemplateRef<unknown>): void {
    this.editingSucursal = null;
    this.form = this.getDefaultForm();
    this.openModal(modalTemplate);
  }

  openEditModal(sucursal: Sucursal, modalTemplate: TemplateRef<unknown>): void {
    this.editingSucursal = sucursal;
    this.form = {
      codigo: sucursal.codigo || '',
      nombre: sucursal.nombre || '',
      direccion: sucursal.direccion || '',
      telefono: sucursal.telefono || '',
      email: sucursal.email || '',
      activo: sucursal.activo,
      es_principal: sucursal.es_principal,
      serie_boleta: sucursal.serie_boleta || 'B001',
      correlativo_boleta: Number(sucursal.correlativo_boleta ?? 0),
      serie_factura: sucursal.serie_factura || 'F001',
      correlativo_factura: Number(sucursal.correlativo_factura ?? 0),
      serie_nota_credito: sucursal.serie_nota_credito || 'NC01',
      correlativo_nota_credito: Number(sucursal.correlativo_nota_credito ?? 0),
      serie_nota_venta: sucursal.serie_nota_venta || 'NV01',
      correlativo_nota_venta: Number(sucursal.correlativo_nota_venta ?? 0),
    };
    this.openModal(modalTemplate);
  }

  openTelefonoModal(
    sucursal: Sucursal,
    modalTemplate: TemplateRef<unknown>,
  ): void {
    this.editingSucursal = sucursal;
    this.openModal(modalTemplate, 'xxl', false);
  }

  closeModal(): void {
    this.modalStack[this.modalStack.length - 1]?.dismiss('manual-close');
  }

  saveSucursal(modal: ModalRef): void {
    if (!this.form.nombre?.trim() || !this.form.codigo?.trim()) {
      this.toastService.show({
        text: 'Nombre y código son obligatorios',
        classname: 'bg-danger text-light',
        delay: 3500,
      });
      return;
    }

    const payload = this.buildPayload();

    if (this.editingSucursal) {
      const changedPayload = this.getChangedPayload(
        this.editingSucursal,
        payload,
      );

      if (Object.keys(changedPayload).length === 0) {
        this.toastService.show({
          text: 'No hay cambios para guardar',
          classname: 'bg-info text-dark',
          delay: 2500,
        });
        return;
      }

      this.sucursalService
        .update(this.empresaId, this.editingSucursal.id, changedPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastService.show({
              text: 'Sede actualizada exitosamente',
              classname: 'bg-success text-light',
              delay: 3000,
            });
            modal.close('saved');
            this.loadSucursales();
          },
          error: (error) => {
            this.toastService.show({
              text: error?.message || 'No se pudo guardar la sede',
              classname: 'bg-danger text-light',
              delay: 4500,
            });
          },
        });

      return;
    }

    this.sucursalService
      .create(this.empresaId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show({
            text: 'Sede creada exitosamente',
            classname: 'bg-success text-light',
            delay: 3000,
          });
          modal.close('saved');
          this.loadSucursales();
        },
        error: (error) => {
          this.toastService.show({
            text: error?.message || 'No se pudo guardar la sede',
            classname: 'bg-danger text-light',
            delay: 4500,
          });
        },
      });
  }

  openDeleteModal(
    sucursal: Sucursal,
    modalTemplate: TemplateRef<unknown>,
  ): void {
    if (sucursal.es_principal) {
      this.toastService.show({
        text: 'No se puede eliminar una sede principal',
        classname: 'bg-warning text-dark',
        delay: 3500,
      });
      return;
    }

    this.deletingSucursal = sucursal;
    this.openModal(modalTemplate, 'md');
  }

  removeSucursal(modal: ModalRef): void {
    const sucursal = this.deletingSucursal;
    if (!sucursal) {
      modal.dismiss('missing-sucursal');
      return;
    }

    this.sucursalService
      .delete(this.empresaId, sucursal.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show({
            text: 'Sede eliminada exitosamente',
            classname: 'bg-success text-light',
            delay: 3000,
          });
          modal.close('deleted');
          this.loadSucursales();
        },
        error: (error) => {
          this.toastService.show({
            text: error?.message || 'No se pudo eliminar la sede',
            classname: 'bg-danger text-light',
            delay: 4500,
          });
          modal.dismiss('delete-error');
        },
      });
  }

  onExcelSelected(event: Event, sucursal: Sucursal): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files?.[0];

    if (!archivo) {
      return;
    }

    const nombreArchivo = archivo.name.toLowerCase();
    const esExcelValido =
      nombreArchivo.endsWith('.xlsx') || nombreArchivo.endsWith('.xls');

    if (!esExcelValido) {
      this.toastService.show({
        text: 'Selecciona un archivo Excel valido (.xlsx o .xls)',
        classname: 'bg-warning text-dark',
        delay: 3500,
      });
      input.value = '';
      return;
    }

    this.importandoSucursalId = sucursal.id;

    this.empresaService
      .importarProductosExcel(this.empresaId, sucursal.id, archivo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resumen: ImportacionProductosResumen) => {
          const totalErrores = resumen.errores?.length ?? 0;
          this.toastService.show({
            text: `Importacion completada en ${sucursal.nombre}: ${resumen.productosCreados} productos creados, ${resumen.categoriasCreadas} categorias creadas, ${totalErrores} errores.`,
            classname:
              totalErrores > 0
                ? 'bg-warning text-dark'
                : 'bg-success text-light',
            delay: totalErrores > 0 ? 5500 : 4000,
          });
          if (resumen.detalles) {
            this.toastService.show({
              text: `Detalles: ${resumen.detalles}`,
              classname: 'bg-info text-dark',
              delay: 7000,
            });
          }
          this.importandoSucursalId = null;
          input.value = '';
        },
        error: (error) => {
          this.toastService.show({
            text:
              error?.message ||
              'No se pudo importar el Excel de productos en esta sede',
            classname: 'bg-danger text-light',
            delay: 5000,
          });
          this.importandoSucursalId = null;
          input.value = '';
        },
      });
  }

  private openModal(
    template: TemplateRef<unknown>,
    size: 'sm' | 'md' | 'xl' | 'lg' | 'xxl' = 'xl',
    resetStateOnClose = true,
  ): void {
    const ref = this.modalService.open(template, {
      size,
      backdrop: true,
    });

    this.modalStack.push(ref);

    ref.result.finally(() => {
      this.modalStack = this.modalStack.filter((modalRef) => modalRef !== ref);

      if (!resetStateOnClose) {
        return;
      }

      this.editingSucursal = null;
      this.deletingSucursal = null;
    });
  }

  private buildPayload(): SucursalPayload {
    return {
      codigo: (this.form.codigo || '').trim().toUpperCase(),
      nombre: (this.form.nombre || '').trim(),
      direccion: this.form.direccion?.trim() || undefined,
      telefono: this.form.telefono?.trim() || undefined,
      email: this.form.email?.trim() || undefined,
      activo: Boolean(this.form.activo),
      es_principal: Boolean(this.form.es_principal),
      serie_boleta: (this.form.serie_boleta || 'B001').trim().toUpperCase(),
      correlativo_boleta: Number(this.form.correlativo_boleta || 0),
      serie_factura: (this.form.serie_factura || 'F001').trim().toUpperCase(),
      correlativo_factura: Number(this.form.correlativo_factura || 0),
      serie_nota_credito: (this.form.serie_nota_credito || 'NC01')
        .trim()
        .toUpperCase(),
      correlativo_nota_credito: Number(this.form.correlativo_nota_credito || 0),
      serie_nota_venta: (this.form.serie_nota_venta || 'NV01')
        .trim()
        .toUpperCase(),
      correlativo_nota_venta: Number(this.form.correlativo_nota_venta || 0),
    };
  }

  private getChangedPayload(
    sucursal: Sucursal,
    payload: SucursalPayload,
  ): Partial<SucursalPayload> {
    const original = this.buildPayloadFromSucursal(sucursal);
    const changedPayload: Partial<SucursalPayload> = {};

    (Object.keys(payload) as Array<keyof SucursalPayload>).forEach((key) => {
      if (payload[key] !== original[key]) {
        (changedPayload as Record<string, unknown>)[key] = payload[key];
      }
    });

    return changedPayload;
  }

  private buildPayloadFromSucursal(sucursal: Sucursal): SucursalPayload {
    return {
      codigo: (sucursal.codigo || '').trim().toUpperCase(),
      nombre: (sucursal.nombre || '').trim(),
      direccion: sucursal.direccion?.trim() || undefined,
      telefono: sucursal.telefono?.trim() || undefined,
      email: sucursal.email?.trim() || undefined,
      activo: Boolean(sucursal.activo),
      es_principal: Boolean(sucursal.es_principal),
      serie_boleta: (sucursal.serie_boleta || 'B001').trim().toUpperCase(),
      correlativo_boleta: Number(sucursal.correlativo_boleta || 0),
      serie_factura: (sucursal.serie_factura || 'F001').trim().toUpperCase(),
      correlativo_factura: Number(sucursal.correlativo_factura || 0),
      serie_nota_credito: (sucursal.serie_nota_credito || 'NC01')
        .trim()
        .toUpperCase(),
      correlativo_nota_credito: Number(sucursal.correlativo_nota_credito || 0),
      serie_nota_venta: (sucursal.serie_nota_venta || 'NV01')
        .trim()
        .toUpperCase(),
      correlativo_nota_venta: Number(sucursal.correlativo_nota_venta || 0),
    };
  }

  private getDefaultForm(): SucursalForm {
    return {
      codigo: '',
      nombre: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true,
      es_principal: false,
      serie_boleta: 'B001',
      correlativo_boleta: 0,
      serie_factura: 'F001',
      correlativo_factura: 0,
      serie_nota_credito: 'NC01',
      correlativo_nota_credito: 0,
      serie_nota_venta: 'NV01',
      correlativo_nota_venta: 0,
    };
  }
}
